'use client';

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePathname } from 'next/navigation';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  resolveAnalyticsStack,
  storefrontCountry,
  type AnalyticsStack,
} from 'lib/analytics/runtime';
import { setConsentGate } from 'lib/consent/gate';
import {
  classifyRoute,
  decide,
  isGtmAllowed,
  releaseOptOut,
  type ConsentCategorySelection,
  type ConsentChoice,
  type ConsentDecision,
} from 'lib/consent/policy';
import {
  captureAdClickIds,
  loadClarity,
  loadCloudflare,
  loadGoogleAds,
  loadGtm,
  loadHubSpot,
  loadMeta,
  loadMixpanel,
  loadReddit,
  loadTikTok,
  loadTripleWhale,
  loadWewomedia,
} from 'lib/analytics/destinations';
import { trackerSkipsStorefront } from 'lib/analytics/trackers';
import {
  consentChoiceOf,
  readConsentRecord,
  writeConsentRecord,
} from './consentCookie';
import type { ConsentRecord } from 'lib/consent/record';
import {
  captureCampaignFromLocation,
  persistCampaign,
} from 'lib/analytics/campaign';
import {
  persistLandingClickIds,
  rememberLandingParams,
} from 'lib/analytics/landingParams';
import { PrivacyChoicesDialog } from './PrivacyChoicesDialog';

export type GrantedCategories = {
  stats: boolean;
  analytics: boolean;
  ads: boolean;
  gtm: boolean;
  replay: boolean;
  attribution: boolean;
};

type ConsentRuntimeValue = {
  stack: AnalyticsStack;
  choice: ConsentChoice;
  consentKnown: boolean;
  gpc: boolean;
  granted: GrantedCategories;
  setBannerReady: (ready: boolean) => void;
  choose: (choice: 'accept' | 'reject') => void;
  openPrivacyChoices: () => void;
  saveCategories: (categories: ConsentCategorySelection) => void;
};

const CLOSED: GrantedCategories = {
  stats: false,
  analytics: false,
  ads: false,
  gtm: false,
  replay: false,
  attribution: false,
};

const ConsentRuntimeContext = createContext<ConsentRuntimeValue>({
  stack: 'legacy',
  choice: 'none',
  consentKnown: false,
  gpc: false,
  granted: CLOSED,
  setBannerReady: () => {},
  choose: () => {},
  openPrivacyChoices: () => {},
  saveCategories: () => {},
});

export function useConsentRuntime(): ConsentRuntimeValue {
  return useContext(ConsentRuntimeContext);
}

function readGpc(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl === true
  );
}

function grantCategories(
  stack: AnalyticsStack,
  known: boolean,
  decision: ConsentDecision,
  choice: ConsentChoice,
  bannerReady: boolean,
): GrantedCategories {
  if (stack !== 'next' || !known) return CLOSED;
  const stats = releaseOptOut(
    decision.modes.stats,
    decision.effective.stats,
    choice,
    bannerReady,
  );
  const analytics = releaseOptOut(
    decision.modes.analytics,
    decision.effective.analytics,
    choice,
    bannerReady,
  );
  const ads = releaseOptOut(
    decision.modes.ads,
    decision.effective.ads,
    choice,
    bannerReady,
  );
  return {
    stats,
    analytics,
    ads,
    replay: decision.effective.replay,
    gtm: isGtmAllowed({ analytics, ads }),
    attribution: decision.effective.attribution,
  };
}

export function ConsentRuntimeProvider({ children }: { children: ReactNode }) {
  const { store, region, detectedRegionFromGeoIP, geoRegion } =
    useRegionLocale();
  const pathname = usePathname() ?? '/';
  const [record, setRecord] = useState<ConsentRecord | null>(null);
  const [consentKnown, setConsentKnown] = useState(false);
  const [gpc, setGpc] = useState(false);
  const [bannerReady, setBannerReady] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    setGpc(readGpc());
    setRecord(readConsentRecord());
    setConsentKnown(true);
  }, []);

  const choice: ConsentChoice = consentChoiceOf(record);

  const stack = resolveAnalyticsStack(store);
  const page = classifyRoute(pathname);
  // The banner waits for state, so the first paint matches the server.
  // The gate does not. A stored choice is read here, before child effects,
  // or a mount event such as view_item is dropped and never retried.
  const liveRecord =
    consentKnown || typeof document === 'undefined' ? null : readConsentRecord();
  const gateRecord = consentKnown ? record : liveRecord;
  const gateGpc = consentKnown ? gpc : readGpc();
  const gateChoice = consentChoiceOf(gateRecord);
  const gateKnown = consentKnown || gateChoice !== 'none';
  const decision = decide({
    country: detectedRegionFromGeoIP,
    province: geoRegion,
    storefrontCountry: stack === 'next' ? storefrontCountry(store) : null,
    choice,
    categories: record?.choice === 'custom' ? record.categories : null,
    gpc,
    page,
    tripleWhale: true,
  });
  const gateDecision = decide({
    country: detectedRegionFromGeoIP,
    province: geoRegion,
    storefrontCountry: stack === 'next' ? storefrontCountry(store) : null,
    choice: gateChoice,
    categories:
      gateRecord?.choice === 'custom' ? gateRecord.categories : null,
    gpc: gateGpc,
    page,
    tripleWhale: true,
  });

  const granted = useMemo<GrantedCategories>(
    () => grantCategories(stack, consentKnown, decision, choice, bannerReady),
    [
      stack,
      consentKnown,
      choice,
      bannerReady,
      decision.modes.stats,
      decision.modes.analytics,
      decision.modes.ads,
      decision.effective.stats,
      decision.effective.analytics,
      decision.effective.ads,
      decision.effective.replay,
      decision.effective.attribution,
    ],
  );
  const gateGranted = useMemo<GrantedCategories>(
    () =>
      grantCategories(stack, gateKnown, gateDecision, gateChoice, bannerReady),
    [
      stack,
      gateKnown,
      gateChoice,
      bannerReady,
      gateDecision.modes.stats,
      gateDecision.modes.analytics,
      gateDecision.modes.ads,
      gateDecision.effective.stats,
      gateDecision.effective.analytics,
      gateDecision.effective.ads,
      gateDecision.effective.replay,
      gateDecision.effective.attribution,
    ],
  );

  useEffect(() => {
    rememberLandingParams(window.location.search);
  }, [pathname]);

  useEffect(() => {
    if (stack !== 'next' || page === 'private') return;
    captureCampaignFromLocation({
      search: window.location.search,
      pathname,
      store,
    });
  }, [stack, page, pathname, store]);

  useEffect(() => {
    if (stack !== 'next' || !granted.ads) return;
    persistCampaign();
    persistLandingClickIds();
  }, [stack, granted.ads]);

  setConsentGate(
    stack === 'legacy'
      ? {
          stack: 'legacy',
          stats: true,
          analytics: true,
          ads: true,
          gtm: true,
          replay: true,
        }
      : { stack: 'next', ...gateGranted },
  );

  const lastRedditPath = useRef<string | null>(null);
  useEffect(() => {
    if (stack !== 'next' || !granted.ads) return;
    if (lastRedditPath.current === null) {
      lastRedditPath.current = pathname;
      return;
    }
    if (pathname === lastRedditPath.current) return;
    lastRedditPath.current = pathname;
    window.rdt?.('track', 'PageVisit');
  }, [stack, granted.ads, pathname]);

  useLayoutEffect(() => {
    if (stack !== 'next' || !gateKnown) return;
    if (gateGranted.analytics) {
      loadMixpanel();
      loadCloudflare();
    }
    if (gateGranted.replay) loadClarity();
    if (gateGranted.gtm) loadGtm(gateGranted.ads);
    if (gateGranted.ads) {
      loadGoogleAds();
      loadMeta();
      loadTikTok();
      loadReddit();
      loadHubSpot();
      if (!trackerSkipsStorefront('wewomedia', region)) loadWewomedia();
      captureAdClickIds();
    }
    if (gateGranted.attribution) loadTripleWhale(store);
  }, [stack, gateKnown, gateGranted, region, store]);

  const choose = useCallback(
    (next: 'accept' | 'reject') => {
      writeConsentRecord({
        v: 1,
        ts: Date.now(),
        store,
        choice: next,
      });
      if (next === 'reject') {
        window.location.reload();
        return;
      }
      setRecord({ v: 1, ts: Date.now(), store, choice: next });
      setDialogOpen(false);
    },
    [store],
  );

  const openPrivacyChoices = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const saveCategories = useCallback(
    (categories: ConsentCategorySelection) => {
      const nextDecision = decide({
        country: detectedRegionFromGeoIP,
        province: geoRegion,
        storefrontCountry: stack === 'next' ? storefrontCountry(store) : null,
        choice: 'custom',
        categories,
        gpc,
        page,
        tripleWhale: true,
      });
      const nextGtm = isGtmAllowed({
        analytics: nextDecision.effective.analytics,
        ads: nextDecision.effective.ads,
      });
      writeConsentRecord({
        v: 1,
        ts: Date.now(),
        store,
        choice: 'custom',
        categories,
      });
      const downgrade =
        (granted.stats && !nextDecision.effective.stats) ||
        (granted.analytics && !nextDecision.effective.analytics) ||
        (granted.ads && !nextDecision.effective.ads) ||
        (granted.replay && !nextDecision.effective.replay) ||
        (granted.gtm && !nextGtm) ||
        (granted.attribution && !nextDecision.effective.attribution);
      if (downgrade) {
        window.location.reload();
        return;
      }
      setRecord({
        v: 1,
        ts: Date.now(),
        store,
        choice: 'custom',
        categories,
      });
      setDialogOpen(false);
    },
    [detectedRegionFromGeoIP, geoRegion, gpc, granted, page, stack, store],
  );

  const value = useMemo<ConsentRuntimeValue>(
    () => ({
      stack,
      choice,
      consentKnown,
      gpc,
      granted,
      setBannerReady,
      choose,
      openPrivacyChoices,
      saveCategories,
    }),
    [
      stack,
      choice,
      consentKnown,
      gpc,
      granted,
      choose,
      openPrivacyChoices,
      saveCategories,
    ],
  );

  return (
    <ConsentRuntimeContext.Provider value={value}>
      <div
        hidden
        data-consent-known={consentKnown ? 'true' : 'false'}
        data-consent-stats={stack === 'next' && granted.stats ? 'true' : 'false'}
        data-consent-analytics={
          stack === 'next' && granted.analytics ? 'true' : 'false'
        }
        data-consent-ads={stack === 'next' && granted.ads ? 'true' : 'false'}
        data-consent-gtm={stack === 'next' && granted.gtm ? 'true' : 'false'}
      />
      {stack === 'next' ? (
        <PrivacyChoicesDialog
          open={dialogOpen}
          gpc={gpc}
          granted={granted}
          onClose={() => setDialogOpen(false)}
          onSave={saveCategories}
        />
      ) : null}
      {children}
    </ConsentRuntimeContext.Provider>
  );
}
